import React, { useEffect, useState } from 'react';
import { HomeReturn, PaymentReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Switch } from '../common/Catalyst/switch';
import { Textarea } from '../common/Catalyst/textarea';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import ShowAlert from '../ShowAlert';
import { billingErrorMessage, formatMoney } from '../utils/billingHelpers';
import { TextSetting } from './BillingPolicyFields';

const MONEY = /^\d+(\.\d{1,2})?$/;

interface Common {
  payment: PaymentReturn | null;
  onClose: () => void;
  onDone: () => void;
}

const useDialog = (payment: PaymentReturn | null) => {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  useEffect(() => {
    setFailure(null);
    setBusy(false);
  }, [payment]);
  return { busy, setBusy, failure, setFailure };
};

const Failure: React.FC<{
  title: string;
  message: string | null;
  onClose: () => void;
}> = ({ title, message, onClose }) =>
  message ? (
    <div className="mb-4">
      <ShowAlert
        alertType="error"
        alertTitle={title}
        alertMessage={message}
        isVisible
        onClose={onClose}
        timeout={14000}
        isInline
      />
    </div>
  ) : null;

// ---------------------------------------------------------------------
// Match a suspense payment to a home
// ---------------------------------------------------------------------

/**
 * Resolve a payment that arrived on an account number we could not
 * read. Matching applies it, so this is the moment it reaches a ledger.
 */
export const MatchPaymentDialog: React.FC<Common & { homes: HomeReturn[] }> = ({
  payment,
  homes,
  onClose,
  onDone,
}) => {
  const { busy, setBusy, failure, setFailure } = useDialog(payment);
  const [homeId, setHomeId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setHomeId('');
    setError('');
  }, [payment]);

  const submit = async (): Promise<void> => {
    if (!homeId) {
      setError('Pick the home this payment belongs to.');
      return;
    }
    if (!payment) return;
    try {
      setBusy(true);
      await billingService.matchPayment({
        paymentId: payment.id,
        paymentMatchRequest: { home_id: homeId },
      });
      onDone();
      onClose();
    } catch (err) {
      console.error('Error matching payment:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!payment} onClose={onClose} size="xl">
      <DialogTitle>Match this payment to a home</DialogTitle>
      <DialogBody>
        <Failure
          title="The payment was not matched"
          message={failure}
          onClose={() => setFailure(null)}
        />
        {payment && (
          <Text className="mb-4">
            {formatMoney(payment.amount)}
            {payment.check_number ? `, cheque #${payment.check_number}` : ''}
            {payment.account_number
              ? `, received on account ${payment.account_number}`
              : ''}
            . Matching applies it straight away, against this home&rsquo;s open
            charges in the association&rsquo;s payment order.
          </Text>
        )}
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Home</Label>
              <Description>
                Every home in this association, whether or not it owes anything.
              </Description>
              <FieldPicker
                label="Home"
                placeholder="Choose a home…"
                value={homeId}
                onChange={(v) => {
                  setHomeId(v);
                  setError('');
                }}
                options={homes.map((h) => ({
                  value: h.id,
                  label: [h.street, h.street2].filter(Boolean).join(' '),
                }))}
              />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="sky" onClick={submit} disabled={busy}>
          {busy ? 'Matching…' : 'Match and apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------
// Return (NSF)
// ---------------------------------------------------------------------

/**
 * A payment that bounced.
 *
 * This unwinds every application it made — the charges it paid open
 * again — and optionally charges the association's NSF fee. Both halves
 * are stated before it happens, because it moves a homeowner from paid
 * to owing plus a fee.
 */
export const ReturnPaymentDialog: React.FC<
  Common & { nsfFee?: string | null }
> = ({ payment, nsfFee, onClose, onDone }) => {
  const { busy, setBusy, failure, setFailure } = useDialog(payment);
  const [reason, setReason] = useState('');
  const [chargeFee, setChargeFee] = useState(true);
  const [feeOverride, setFeeOverride] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setReason('');
    setChargeFee(true);
    setFeeOverride('');
    setErrors({});
  }, [payment]);

  const submit = async (): Promise<void> => {
    const found: Record<string, string> = {};
    if (!reason.trim()) {
      found.reason = 'A reason is required — it stays on the payment.';
    }
    if (chargeFee && feeOverride && !MONEY.test(feeOverride.trim())) {
      found.fee = 'Enter an amount, or leave blank for the standard fee.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0 || !payment) return;

    try {
      setBusy(true);
      await billingService.returnPayment({
        paymentId: payment.id,
        paymentReturnRequest: {
          reason: reason.trim(),
          charge_nsf_fee: chargeFee,
          nsf_fee_amount: chargeFee && feeOverride ? feeOverride.trim() : null,
        },
      });
      onDone();
      onClose();
    } catch (err) {
      console.error('Error returning payment:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const applied = payment
    ? payment.applications.filter((a) => !a.reversed_at).length
    : 0;

  return (
    <Dialog open={!!payment} onClose={onClose} size="xl">
      <DialogTitle>Record this payment as returned</DialogTitle>
      <DialogBody>
        <Failure
          title="The payment was not returned"
          message={failure}
          onClose={() => setFailure(null)}
        />
        {payment && (
          <Text className="mb-4">
            This reverses{' '}
            <strong>
              {applied} {applied === 1 ? 'application' : 'applications'}
            </strong>{' '}
            of {formatMoney(payment.amount)}, so every charge it paid is open
            again. The payment stays on the record with its applications marked
            reversed — that is how anybody later answers which charges this
            cheque had paid.
          </Text>
        )}
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Reason</Label>
              <Description>
                Insufficient funds, account closed, stopped payment.
              </Description>
              <Textarea
                rows={2}
                value={reason}
                invalid={!!errors.reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setErrors({});
                }}
              />
              {errors.reason && <ErrorMessage>{errors.reason}</ErrorMessage>}
            </Field>

            <Field className="flex items-start justify-between gap-4">
              <div>
                <Label>Charge the NSF fee</Label>
                <Description>
                  {nsfFee
                    ? `This association charges ${formatMoney(nsfFee)}.`
                    : 'This association has no NSF fee configured, so nothing will be charged.'}
                </Description>
              </div>
              <Switch
                checked={chargeFee}
                onChange={setChargeFee}
                aria-label="Charge the NSF fee"
              />
            </Field>

            {chargeFee && (
              <TextSetting
                label="Fee amount"
                help="Leave blank to use the association’s standard fee. Several states cap this."
                prefix="$"
                placeholder={nsfFee ?? 'None configured'}
                value={feeOverride}
                error={errors.fee}
                onChange={setFeeOverride}
              />
            )}
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="red" onClick={submit} disabled={busy}>
          {busy ? 'Recording…' : 'Record as returned'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------
// Assess the returned-payment fee afterwards
// ---------------------------------------------------------------------

/**
 * Charge the association's returned-payment fee to a payment that is
 * already `returned` — because the chargeback job reversed it.
 *
 * WHY THIS EXISTS SEPARATELY FROM "Returned (NSF)". The job reverses a
 * bounced payment unattended and charges nothing: whether this bounce
 * deserves the HOA's fee is a judgement a job does not have. The
 * return dialog cannot be reused — the server refuses to return a
 * payment twice — so this is the deliberate, separately audited act
 * that adds the fee afterwards. Once, ever, per payment.
 *
 * Two different numbers are in play and this dialog names only one:
 * what the HOA charges the homeowner (policy). What the bank charged
 * the association is on the chargeback row, and is not the homeowner's
 * to pay unless the HOA's policy says so.
 */
export const AssessFeeDialog: React.FC<Common & { nsfFee?: string | null }> = ({
  payment,
  nsfFee,
  onClose,
  onDone,
}) => {
  const { busy, setBusy, failure, setFailure } = useDialog(payment);
  const [feeOverride, setFeeOverride] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFeeOverride('');
    setError(null);
  }, [payment]);

  const submit = async (): Promise<void> => {
    if (feeOverride && !MONEY.test(feeOverride.trim())) {
      setError('Enter an amount, or leave blank for the standard fee.');
      return;
    }
    if (!nsfFee && !feeOverride.trim()) {
      setError(
        'This association has no returned-payment fee configured. Enter the amount to charge.'
      );
      return;
    }
    setError(null);
    if (!payment) return;
    try {
      setBusy(true);
      await billingService.assessNsfFee({
        paymentId: payment.id,
        nsfFeeRequest: {
          nsf_fee_amount: feeOverride ? feeOverride.trim() : null,
        },
      });
      onDone();
      onClose();
    } catch (err) {
      console.error('Error assessing fee:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!payment} onClose={onClose} size="xl">
      <DialogTitle>Charge the returned-payment fee</DialogTitle>
      <DialogBody>
        <Failure
          title="The fee was not charged"
          message={failure}
          onClose={() => setFailure(null)}
        />
        {payment && (
          <Text className="mb-4">
            This payment of {formatMoney(payment.amount)} was returned and no
            fee was charged at the time. Charging it now adds one fee entry to
            the home’s ledger. It can be done once; reversing it afterwards is a
            ledger correction, not an undo.
          </Text>
        )}
        <Fieldset>
          <FieldGroup>
            <TextSetting
              label="Fee amount"
              help={
                nsfFee
                  ? `Leave blank to charge the association’s standard fee of ${formatMoney(nsfFee)}. Several states cap this.`
                  : 'This association has no standard fee configured.'
              }
              prefix="$"
              placeholder={nsfFee ?? 'None configured'}
              value={feeOverride}
              error={error ?? undefined}
              onChange={(v) => {
                setFeeOverride(v);
                setError(null);
              }}
            />
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="red" onClick={submit} disabled={busy}>
          {busy ? 'Charging…' : 'Charge the fee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------
// Refund the held remainder
// ---------------------------------------------------------------------

/**
 * Give back what could not be applied.
 *
 * **Only the held remainder.** The applied part paid real charges and
 * is not ours to hand back without reversing those first, which is a
 * different act — so the dialog names the exact figure rather than
 * talking about "the payment".
 */
export const RefundPaymentDialog: React.FC<Common> = ({
  payment,
  onClose,
  onDone,
}) => {
  const { busy, setBusy, failure, setFailure } = useDialog(payment);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setReason('');
    setError('');
  }, [payment]);

  const submit = async (): Promise<void> => {
    if (!reason.trim()) {
      setError('A reason is required — it stays on the payment.');
      return;
    }
    if (!payment) return;
    try {
      setBusy(true);
      await billingService.refundPayment({
        paymentId: payment.id,
        paymentRefundRequest: { reason: reason.trim() },
      });
      onDone();
      onClose();
    } catch (err) {
      console.error('Error refunding payment:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!payment} onClose={onClose} size="xl">
      <DialogTitle>Refund the held amount</DialogTitle>
      <DialogBody>
        <Failure
          title="The refund was not recorded"
          message={failure}
          onClose={() => setFailure(null)}
        />
        {payment && (
          <Text className="mb-4">
            This refunds{' '}
            <strong>{formatMoney(payment.amount_unapplied)}</strong> — the part
            of {formatMoney(payment.amount)} that was never applied to a charge.
            What it did pay stays paid; giving that back would mean reversing
            those charges first, which is a separate decision.
          </Text>
        )}
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Reason</Label>
              <Description>
                Recorded on the payment. Note how the money was actually sent
                back, since this records the decision rather than moving it.
              </Description>
              <Textarea
                rows={2}
                value={reason}
                invalid={!!error}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
              />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="sky" onClick={submit} disabled={busy}>
          {busy
            ? 'Recording…'
            : `Refund ${formatMoney(payment?.amount_unapplied)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
